import { createApp } from "vue";
import { SiteType } from "@du/appEnum/SiteType";
import { setupBugReportSystem } from "@utility/bugReportSetup";
import type ILegacyFunctionCall from "./interfaces/ILegacyFunctionCall";
import {
  importCustomerPortalCssBundles,
  importCustomerPortalJsBundles,
  importFormBuilderJsBundles,
  importS1WebLegacyCssBundles,
  importS1WebLegacyJsBundles,
} from "./LegacyBundleImports";

/**
 * Add visual bug report button to legacy pages
 */
const addBugReportButton = () => {
  // Create button wrapper with fixed positioning
  const buttonWrapper = document.createElement("div");
  buttonWrapper.className = "bug-report-button-wrapper";
  buttonWrapper.style.cssText = `
    position: fixed;
    bottom: 20px;
    right: 20px;
    z-index: 10000;
  `;

  // Create the button element
  const button = document.createElement("button");
  button.className = "btn btn-primary report-bug-button";
  button.style.cssText = `
    border-radius: 50%;
    padding: 15px;
    width: 50px;
    height: 50px;
    background-color: var(--primary-button-bg-color, #0068b3);
    color: #fff;
    border: none;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    white-space: nowrap;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    transition: width 0.3s ease, border-radius 0.3s ease, box-shadow 0.2s ease, transform 0.2s ease;
  `;

  // Create icon element (FontAwesome flag icon)
  const icon = document.createElement("i");
  icon.className = "fa fa-flag";
  icon.setAttribute("aria-hidden", "true");
  icon.style.flexShrink = "0";

  // Create text span (hidden by default, shown on hover)
  const buttonText = document.createElement("span");
  buttonText.textContent = "Report Bug";
  buttonText.style.cssText = `
    max-width: 0;
    opacity: 0;
    overflow: hidden;
    margin-left: 0;
    transition: max-width 0.3s ease, opacity 0.2s ease, margin-left 0.3s ease;
  `;

  // Add hover effect - expand to show text
  button.addEventListener("mouseenter", () => {
    button.style.width = "auto";
    button.style.borderRadius = "30px";
    button.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.2)";
    button.style.transform = "translateY(-2px)";
    buttonText.style.maxWidth = "100px";
    buttonText.style.opacity = "1";
    buttonText.style.marginLeft = "8px";
  });

  button.addEventListener("mouseleave", () => {
    button.style.width = "50px";
    button.style.borderRadius = "50%";
    button.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.15)";
    button.style.transform = "translateY(0)";
    buttonText.style.maxWidth = "0";
    buttonText.style.opacity = "0";
    buttonText.style.marginLeft = "0";
  });

  // Assemble button
  button.appendChild(icon);
  button.appendChild(buttonText);

  // Add click handler to open bug report modal
  button.addEventListener("click", () => {
    if (typeof window["openBugReport"] === "function") {
      window["openBugReport"]();
    }
  });

  // Assemble and add to page
  buttonWrapper.appendChild(button);
  document.body.appendChild(buttonWrapper);

  console.info("[Bug Report] Report button added to legacy page");
};

/**
 * Add global bug report modal to legacy pages
 * Can be triggered via Ctrl+Shift+B or window.openBugReport()
 */
let bugReportInitialized = false;
const addGlobalBugReportModal = async (bottomOffset?: string) => {
  // Only initialize once
  if (bugReportInitialized) {
    return;
  }
  bugReportInitialized = true;

  // Initialize bug report system (activity tracking, error handlers)
  setupBugReportSystem();

  // Create mount point for bug report modal
  const bugReportContainer = document.createElement("div");
  bugReportContainer.id = "bug-report-container";
  document.body.appendChild(bugReportContainer);

  // Dynamically import and mount the global bug report modal
  const { default: globalBugReportModal } = await import(
    "@compUtility/bugReportTool/globalBugReportModal.vue"
  );

  const bugReportApp = createApp(globalBugReportModal);
  bugReportApp.mount("#bug-report-container");

  // Add visual bug report button
  addBugReportButton();

  console.info("[Bug Report] Global modal ready on legacy page. Press Ctrl+Shift+B to open.");
};

const execLegacyS1Func = (legacyFuncArgs: ILegacyFunctionCall): void => {
  ensureBundle("css", legacyFuncArgs.initFunctionLibName);
  ensureBundle("js", legacyFuncArgs.initFunctionLibName).then(() => {
    const legacyFuncCallText = `${legacyFuncArgs.initFunctionLibName}(${legacyFuncArgs.initArgs
      ?.map((m) => JSON.stringify(m))
      ?.join(",")})`;
    try {
      const user = window["hana"]?.appStartArgs?.CurrentUserObj;
      const siteTypeId = window["hana"]?.appStartArgs?.SiteTypeId;

      new Function(legacyFuncCallText)();

      // Add global bug report modal (S1 only) after legacy initialization
      if (siteTypeId === SiteType.S1) {
        addGlobalBugReportModal();
      }
    } catch (e) {
      console.error(
        `Error executing legacy function: ${legacyFuncArgs.initFunctionLibName}, ${
          e && e["stack"] ? "stack: " + e["stack"] : ""
        } full args:`,
        legacyFuncCallText
      );
    }
  });
};

const TICKER_DISPLAY_LIB = "hana.epc.tickerdisplay";
const FORM_BUILDER_INIT_FUNCTIONS = new Set<string>(["hana.pages.formBuilderEditor.init"]);

const ensureBundle = (bundleType: "css" | "js", libraryFunction: string): Promise<any> => {
  const parts = libraryFunction.split(".");
  if (parts.length <= 1) {
    return Promise.resolve();
  }

  //load the formbuilder bundles after the man lib
  if (bundleType === "js" && FORM_BUILDER_INIT_FUNCTIONS.has(libraryFunction)) {
    return importS1WebLegacyJsBundles(libraryFunction).then(() => importFormBuilderJsBundles());
  }

  let libName = parts[0];

  //load the EPC specialized bundle
  if (libraryFunction === "hana.pages.tickerDisplay.init") {
    libName = TICKER_DISPLAY_LIB;
  }

  if (libName === "hana" || libName === TICKER_DISPLAY_LIB) {
    return bundleType === "css" ? importS1WebLegacyCssBundles() : importS1WebLegacyJsBundles(libraryFunction);
  } else if (libName === "cw") {
    return bundleType === "css" ? importCustomerPortalCssBundles() : importCustomerPortalJsBundles();
  }

  return Promise.resolve();
};

export { execLegacyS1Func };
